import { useState, useEffect } from 'react';
import axios from 'axios';

export const useTags = (projectId) => {
  // Tag management state
  const [projectTags, setProjectTags] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedChatForTagging, setSelectedChatForTagging] = useState(null);
  const [isCreatingNewTag, setIsCreatingNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [newTagIcon, setNewTagIcon] = useState('tag');

  // Filter state
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterMenuPosition, setFilterMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);

  // Tag management functions
  const fetchProjectTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/tags`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjectTags(response.data.tags || []);
    } catch (error) {
      console.error('Error fetching project tags:', error);
    }
  };

  const createTag = async (name, color, icon = 'tag') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/tags`,
        { name, color, icon },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newTag = response.data.tag;
      setProjectTags(prev => [...prev, newTag]);
      return newTag;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  };

  const assignTagToChat = async (chatId, tagId, tagObject = null) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/tags`,
        { tagId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return tagObject || projectTags.find(t => t.id === tagId);
    } catch (error) {
      console.error('Error assigning tag to chat:', error);
      throw error;
    }
  };

  const removeTagFromChat = async (chatId, tagId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/tags/${tagId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error removing tag from chat:', error);
      throw error;
    }
  };

  const handleTagClick = (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedChatForTagging(chatId);
    setShowTagModal(true);
  };

  const handleTagSelection = async (tagId) => {
    if (!selectedChatForTagging) return;
    
    try {
      const tag = await assignTagToChat(selectedChatForTagging, tagId);
      setShowTagModal(false);
      setSelectedChatForTagging(null);
      return tag;
    } catch (error) {
      throw new Error('Failed to assign tag to chat');
    }
  };

  const handleCreateNewTag = async () => {
    if (!newTagName.trim()) return;
    
    try {
      const newTag = await createTag(newTagName.trim(), newTagColor, newTagIcon);
      // Pass the newly created tag object directly to avoid state timing issues
      await assignTagToChat(selectedChatForTagging, newTag.id, newTag);
      
      // Reset form
      setNewTagName('');
      setNewTagColor('#3B82F6');
      setNewTagIcon('tag');
      setIsCreatingNewTag(false);
      setShowTagModal(false);
      setSelectedChatForTagging(null);
      
      return newTag;
    } catch (error) {
      throw new Error('Failed to create and assign tag');
    }
  };

  // Filter functions
  const handleFilterByTag = (tagId) => {
    setSelectedTagFilter(tagId);
    setShowFilterMenu(false);
  };

  const handleFilterMenuClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFilterMenu(true);
    setFilterMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const clearFilter = () => {
    setSelectedTagFilter(null);
    setShowFilterMenu(false);
  };

  return {
    projectTags,
    showTagModal,
    selectedChatForTagging,
    isCreatingNewTag,
    newTagName,
    newTagColor,
    newTagIcon,
    showFilterMenu,
    filterMenuPosition,
    selectedTagFilter,
    fetchProjectTags,
    createTag,
    assignTagToChat,
    removeTagFromChat,
    handleTagClick,
    handleTagSelection,
    handleCreateNewTag,
    handleFilterByTag,
    handleFilterMenuClick,
    clearFilter,
    setShowTagModal,
    setSelectedChatForTagging,
    setIsCreatingNewTag,
    setNewTagName,
    setNewTagColor,
    setNewTagIcon,
    setShowFilterMenu
  };
}; 